import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  HStack,
  useMediaQuery,
  Divider,
  Image,
} from "@chakra-ui/react";
import { Images } from "../../Assets/images";
import React, { useEffect, useState } from "react";
import {
  MdOutlineLocationOn,
  MdOutlineCalendarMonth,
  MdOutlineHandshake,
  MdAutoAwesome,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { errorHandler } from "../../Utils/handler";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  MEETING_REQUEST_STATUS,
  MeetingsField,
  RelevanceFieldsNew,
  Buyerscreens,
} from "../../Constants/data";
import Jumbotron from "../../Components/Common/Jumbotron";
import CountDisplay from "../../Components/Common/CountDisplay";
import { CommonAPI } from "../../Apis/commonApi";
import MeetingListItem from "../../Components/Common/MeetingListItem";
import EventListItem from "../../Components/Common/EventListItem";
import actions from "../../redux/Actions";
import { BuyerAPI } from "../../Apis/buyerAPI";
import { RfxAPI } from "../../Apis/rfxAPI";

type Props = {};

const Dashboard = (props: Props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [data, setData] = useState<any>();
  const user = useSelector((state: any) => state.user);
  const buyerMenu = useSelector((state: any) => state.buyerMenu);
  const [isScreenWidthSufficient] = useMediaQuery(["(min-width: 490px)"]);
  const [profile, setProfile] = useState<any>();
  const [rewardsClosingBalance, setRewardsClosingBalance] = useState<any>({});
  const { success, error, info } = useToastWrapper();
  const [meetingCounts, setMeetingCounts] = useState<any>({});
  const [rfxStats, setRfxStats] = useState<{ open: number; closed: number; total: number }>({ open: 0, closed: 0, total: 0 });

  const getData = async () => {
    try {
      // Claude: added RFX stats to dashboard data fetches 25-02-2026
      const [res, res2, res3, res4] = await Promise.all([
        BuyerAPI.GetDashboard(),
        BuyerAPI.getBuyerProfile(),
        CommonAPI.GetMeeting(undefined),
        RfxAPI.getBuyerRfxStats(),
      ]);
      if (res) {
        setData(res.data.data);
      }
      if (res2) {
        setProfile(res2.data.data);
        // Claude: sync verificationStatus to Redux on each dashboard load — no re-login needed 10-03-2026
        dispatch(actions.user.setVerificationStatus({
          verificationStatus: res2.data.data?.verificationStatus,
          verificationDeadline: res2.data.data?.verificationDeadline,
        }));
        const params: any = {};
        params["buyerId"] = res2.data.data._id;
        const [rewards] = await Promise.all([
          BuyerAPI.getRewardsByBuyerId(params),
        ]);
        setRewardsClosingBalance(rewards.data.data.rewards);
      }
      if (res3) {
        setMeetingCounts(res3.data.meetingCounts);
      }
      if (res4) {
        setRfxStats(res4.data);
      }
    } catch (err) {
      error(errorHandler(err));
    }
  };

  useEffect(() => {
    getData();
    // Claude: N23 — force close AI chat on dashboard mount (clears any persisted open state) 04-03-2026
    const { verificationStatus: vStatus } = user;
    dispatch(actions.buyerMenu.set({
      activeMenu: Buyerscreens.HOME,
      aiChatOpen: false,
      aiChatContext: (vStatus === "unverified" || vStatus === "provisional")
        ? `dashboard|Your account status is ${vStatus}. Please gently remind the user to visit their profile and complete verification to avoid account restrictions.`
        : "dashboard",
    }));
  }, []);

  const unreadNotifications = useSelector((state: any) => state.notification.unreadNotifications);

  // Claude: verification notice helpers 03-03-2026
  const { verificationStatus, verificationDeadline } = user;
  const verifDaysRemaining = verificationDeadline
    ? Math.max(0, Math.ceil((new Date(verificationDeadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : null;
  const verifDeadlineDate = verificationDeadline ? (() => {
    const d = new Date(verificationDeadline);
    return `${String(d.getDate()).padStart(2, '0')}-${d.toLocaleDateString('en-GB', { month: 'short' })}-${d.getFullYear()}`;
  })() : null;

  const disableUpcomingMeetingButton = (
    proposedDateStr: string,
    proposedTime: string
  ) => {
    const today = new Date();
    const proposedDate = new Date(proposedDateStr);

    if (
      today.getDay() === proposedDate.getDay() &&
      today.getMonth() === proposedDate.getMonth() &&
      today.getFullYear() === proposedDate.getFullYear() &&
      today.getHours() >= parseInt(proposedTime.split(":")[0]) &&
      today.getMinutes() >= parseInt(proposedTime.split(":")[1])
    ) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <Box w={"100%"} p={"0"}>
      {/* Claude: dark header — replaces Jumbotron on dashboard 27-03-2026 */}
      <Flex w={"100%"} px={"30px"} py={5} bg={"#0E122F"} color={"white"} alignItems={"center"} justifyContent={"space-between"}>
        <Text fontSize={"xl"} fontWeight={700}>Dashboard</Text>
        <VStack spacing={0} alignItems="center" justifyContent="center">
          <Image src={Images.AftrLogo} h="32px" objectFit="contain" />
          <Text fontSize="11px" fontWeight={300} color="whiteAlpha.600" letterSpacing="wide">buyer (beta)</Text>
        </VStack>
      </Flex>
      {/* Claude: verification status banner — visible on dashboard, unverified/provisional/resubmitted 03-03-2026 */}
      {(verificationStatus === "unverified" || verificationStatus === "provisional" || verificationStatus === "resubmitted") && (
        <Box
          bg="#FEE2E2"
          border="1px solid #C62828"
          borderRadius="8px"
          px={4}
          py={3}
          mx={4}
          mt={3}
          cursor="pointer"
          onClick={() => navigate("/buyer/profile")}
        >
          <Text fontSize="sm" color="#C62828" fontWeight={700}>
            {verificationStatus === "unverified" ? "Account Unverified"
              : verificationStatus === "resubmitted" ? "Profile Submitted — Pending Verification"
              : "Account Provisional — Under Review"}
          </Text>
          {/* Claude: clickable support email 19-03-2026 */}
          <Text fontSize="xs" color="#C62828" mt={1}>
            {verificationStatus === "unverified" ? (
              <>{`Profile verification requires a valid Company Website matching your Work Email domain.${verifDaysRemaining !== null ? ` Restricted in ${verifDaysRemaining} day${verifDaysRemaining !== 1 ? "s" : ""}${verifDeadlineDate ? ` (${verifDeadlineDate})` : ""}.` : ""} Tap to update profile.`}</>
            ) : verificationStatus === "resubmitted" ? (
              <>Your profile has been submitted for verification. Our team will review it shortly. Contact{" "}
                <Text as="a" href="mailto:support@aftr.network" textDecoration="underline">support@aftr.network</Text>
                {" "}for details.</>
            ) : (
              <>Your profile is being reviewed. Tap to view your profile or contact{" "}
                <Text as="a" href="mailto:support@aftr.network" textDecoration="underline">support@aftr.network</Text>.</>
            )}
          </Text>
        </Box>
      )}
      <Box
        w={"100%"}
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        px={"30px"}
        backgroundColor={"body.bg"}
      >

        {/* Claude: Removed "Recent Events" title per request 22-02-2026 */}
        <Box w={"100%"} mt={4}>
          <Box
            mt={"12px"}
            p={"16px"}
            _hover={{ shadow: "md" }}
            border={"1px solid #D2D2D2"}
            borderRadius={"15px"}
            shadow={"sm"}
            backgroundColor={"body.cardBg"}
          >
            {/* Claude: removed recentOrOngoingEvent block — replaced by My Events list below 04-03-2026 */}
            {/* Rewards section */}
            {rewardsClosingBalance.length > 0 && (
              <>
                <Divider borderColor="black" borderWidth="2px" />
                <HStack mt="6px" mb="6px" justifyContent="center">
                  <Text fontSize={"sm"} fontWeight={"medium"} textAlign="center">
                    Reward Points in Hand: {rewardsClosingBalance[0].closingBalance}.
                  </Text>
                  <Button
                    onClick={() => navigate("/buyer/rewards")}
                    colorScheme={"gray"}
                    variant={"outline"}
                    color={"secondary.orange"}
                    _hover={{ bg: "#2E7D32", color: "white", borderColor: "#2E7D32" }}
                    h={"26px"} minH={"26px"} px={2} size="sm"
                  >
                    View
                  </Button>
                </HStack>
                <Divider borderColor="black" borderWidth="2px" />
              </>
            )}
            {/* Claude: RFX stats — tightened spacing 04-03-2026 */}
            <Flex justifyContent={"space-between"} mt={2}>
              <Text fontSize={"md"} fontWeight={"medium"}>My RFXs</Text>
            </Flex>
            <Flex mt={2} justifyContent={"space-between"} gap={"0 .25rem"}>
              <CountDisplay width="33%" title="Open" count={String(rfxStats.open)} color={"meetings.confirmed"} />
              <CountDisplay width="33%" title="Closed" count={String(rfxStats.closed)} color={"meetings.received"} />
              <CountDisplay width="33%" title="Total" count={String(rfxStats.total)} color={"meetings.sent"} />
            </Flex>
            <HStack justifyContent={"center"}>
              <Button
                onClick={() => { navigate("/buyer/rfxs"); }}
                colorScheme={"gray"} variant={"outline"} color={"secondary.orange"}
                _hover={{ bg: "#2E7D32", color: "white", borderColor: "#2E7D32" }}
                mt={2} h={"32px"} minH={"32px"} fontSize="sm" px={3}
              >
                View My RFXs
              </Button>
            </HStack>

            {/* Claude: Sellers bar */}
            <Flex justifyContent={"space-between"} mt={3}>
              <Text fontSize={"md"} fontWeight={"medium"}>Sellers</Text>
            </Flex>
            {/* Claude: flexWrap 2x2 grid on mobile 28-03-2026 */}
            <Flex mt={2} flexWrap="wrap" gap={2}>
              <CountDisplay width="calc(50% - 4px)" title={RelevanceFieldsNew.RELEVANT.title} count={data?.recentOrOngoingEventWithSellers?.relevantSellersCount} color={"buyers.relevant"} />
              <CountDisplay width="calc(50% - 4px)" title={RelevanceFieldsNew.REVIEW.title} count={data?.recentOrOngoingEventWithSellers?.underResearchSellersCount} color={"buyers.review"} />
              <CountDisplay width="calc(50% - 4px)" title={RelevanceFieldsNew.EXISTING.title} count={data?.recentOrOngoingEventWithSellers?.existingRelationshipSellersCount} color={"buyers.existing"} />
              <CountDisplay width="calc(50% - 4px)" title={RelevanceFieldsNew.NOTRELEVANT.title} count={data?.recentOrOngoingEventWithSellers?.notRelevantSellersCount} color={"buyers.notRelevant"} />
            </Flex>
            <HStack justifyContent={"center"}>
              <Button
                onClick={() => { navigate("/buyer/list"); }}
                colorScheme={"gray"} variant={"outline"} color={"secondary.orange"}
                _hover={{ bg: "#2E7D32", color: "white", borderColor: "#2E7D32" }}
                mt={2} h={"32px"} minH={"32px"} fontSize="sm" px={3}
              >
                Browse Sellers
              </Button>
            </HStack>

            {/* Claude: Meetings bar */}
            <Flex justifyContent={"space-between"} mt={3}>
              <Text fontSize={"md"} fontWeight={"medium"}>Meetings</Text>
            </Flex>
            {/* Claude: flexWrap 2x2 grid on mobile 28-03-2026 */}
            <Flex mt={2} flexWrap="wrap" gap={2}>
              <CountDisplay width="calc(50% - 4px)" title={MeetingsField.CONFIRMED.title} count={meetingCounts[MEETING_REQUEST_STATUS.CONFIRM] ?? 0} color={"meetings.confirmed"} />
              <CountDisplay width="calc(50% - 4px)" title={MeetingsField.RECEIVED.title} count={meetingCounts[MEETING_REQUEST_STATUS.REQUEST_RECEIVED] ?? 0} color={"meetings.received"} />
              <CountDisplay width="calc(50% - 4px)" title={MeetingsField.SENT.title} count={meetingCounts[MEETING_REQUEST_STATUS.RESPONSE_AWAITED] ?? 0} color={"meetings.sent"} />
              <CountDisplay width="calc(50% - 4px)" title={MeetingsField.COMPLETED.title} count={meetingCounts[MEETING_REQUEST_STATUS.CLOSED] ?? 0} color={"meetings.completed"} />
            </Flex>
            <HStack justifyContent={"center"}>
              <Button
                onClick={() => { navigate("/buyer/calendar"); }}
                colorScheme={"gray"} variant={"outline"} color={"secondary.orange"}
                _hover={{ bg: "#2E7D32", color: "white", borderColor: "#2E7D32" }}
                mt={2} h={"32px"} minH={"32px"} fontSize="sm" px={3}
              >
                View All Meetings
              </Button>
            </HStack>

            <Text mt={3} fontSize={"md"} fontWeight={"medium"}>
              Upcoming Meeting
            </Text>
            {data?.upcomingMeeting ? (
              <Box mt={3} mb={3}>
                <MeetingListItem
                  companyName={
                    user.user == data?.upcomingMeeting?.requesterId
                      ? data?.upcomingMeeting?.responderCompany
                      : data?.upcomingMeeting?.requesterCompany
                  }
                  proposedDate={moment(
                    data?.upcomingMeeting?.proposedDate
                  )?.format("DD/MMM/YYYY")}
                  proposedTime={data?.upcomingMeeting?.proposedTime}
                  onClick={() => navigate("/buyer/calendar")} // Claude: navigate to meetings list 13-04-2026
                />
              </Box>
            ) : (
              <Box>
                {/* <Text flex={1} fontSize={"sm"} fontWeight={500}>
                                    No confirmed meetings yet. Please see all meeting list to review
                                    pending requests.
                                </Text> */}
              </Box>
            )}
          </Box>
        </Box>
        {/* Claude: Commented out Upcoming Event section per request 16-02-2026 */}
        {/* Claude: My Events — all events descending by startDate; dateless events in random order 04-03-2026 */}
        <Box w={"100%"} mt={6} mb={"30px"}>
          <Text fontSize={"lg"} fontWeight={"semibold"}>
            My Events
          </Text>
          {data?.myAllEvents?.length > 0 ? (
            [...(data.myAllEvents)].sort((a: any, b: any) => {
              if (!a.startDate && !b.startDate) return 0;
              if (!a.startDate) return 1;
              if (!b.startDate) return -1;
              return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
            }).map((event: any) => (
              <EventListItem
                key={event._id}
                eventName={event.eventName}
                startDate={event.startDate}
                endDate={event.endDate}
                eventData={{ eventVenue: event.eventVenue, eventCity: event.eventCity }}
                orgCompanyName={event.organizerId?.organizerCompanyName}
              />
            ))
          ) : (
            <Box mt={"10px"} p={"30px"} border={"1px solid #E2E8F0"} borderRadius={"15px"} shadow={"sm"}>
              <Text fontSize={"md"} fontWeight={500}>No events yet</Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard;
